import React, { useState, useEffect } from 'react';
import { Navigate } from "react-router-dom";
import { GlasswareType, GravimetricCalculationType, NoteType, ResultCodes, Entities, DocumentType } from '../models/enums';
import { Note } from '../models/note';
import { BatchProduction } from '../models/batch-production';
import { Asset } from '../models/asset';
import { FieldUpdate } from '../models/field-update';
import { CheckWeight } from '../models/check-weight';
import { BatchCheckWeightData } from '../models/batch-check-weight-data';
import { ItemBasicInformation } from '../models/item-basic-information';
import { TargetBatchWeightCalculation } from '../models/Testing/target-batch-weight-calculation';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import BenchSheetToolbar from './BenchSheetToolbar';
import TicketLogEntryForm from './TicketLogEntryForm';
import { DataTemplates } from '../templates/data-templates';
import GlasswareSearch from './GlasswareSearch';
import DocumentLoader from './DocumentLoader';
import ComponentsActualDataComponent from './ComponentsActualDataComponent';
import ProductionBenchSheetSolventsDataComponent from './ProductionBenchSheetSolventsDataComponent';
import CheckWeightsComponent from './CheckWeightsComponent';
import AppearanceBenchSheetComponent from './BenchSheets/AppearanceBenchSheetComponent';
import { PWDates } from '../utilities/pw-dates';
import { CrudFunctions } from '../utilities/crud-functions';
import { UserDataStore } from '../store/userDataStore';
import NoteViewer from './NoteViewer';
import ProductionBenchSheetPlanningNotesViewer from './ProductionBenchSheetPlanningNotesViewer';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { DocumentationConstants } from '../store/documentation-constants';
import AuditHistoryComponent from './AuditHistoryComponent';
import DmaFooter from './DmaFooter';
import InstrumentSelectBoxTooltip from './InstrumentSelectBoxTooltip';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Button as ColumnButton,
    Toolbar,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import DateBox from 'devextreme-react/date-box';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import Toast from 'devextreme-react/toast';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;
let componentsWeight = 0;

export default function BatchProductionForm() {
    const [batch, setBatch] =
        useState<BatchProduction>();

    const [balances, setBalances] =
        useState<Asset[]>();

    const [dispensers, setDispensers] =
        useState<Asset[]>();

    const [targetBatchWeightCalculation, setTargetBatchWeightCalculation] =
        useState<TargetBatchWeightCalculation>();

    const [matrixWeight, setMatrixWeight] =
        useState<number>();

    const [checkWeightData, setCheckWeightData] =
        useState<BatchCheckWeightData>();

    const [auditEntity, setAuditEntity] =
        useState<Entities>();

    const [auditRowPointer, setAuditRowPointer] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [redirectToProductFrom, setRedirectToProductFrom] =
        useState<boolean>();

    const [volumetricFlaskSearchDialogVisible, setVolumetricFlaskSearchDialogVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [toastVisible, setToastVisible] =
        useState<boolean>();

    const [toastMessage, setToastMessage] =
        useState<string>();

    const [toastType, setToastType] =
        useState<any>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
        initSetTabs();
    }, []);

    const parts = window.location.href.split('/');
    const batchId = parts[parts.length - 1];

    key++;

    if (redirectToProductFrom && batch &&
        batch.productData && batch.productData.productId) {
        const prodUrl = `/product/${batch.productData.productId}`;

        return <Navigate to={prodUrl} replace={true} />;
    }

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "PBS-A", "PBS-L", "PBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "PBS-A", "PBS-L"];
    const canInsert = canInsertRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "PBS-A"];
    const canDelete = canDeleteRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    let batchDensityTitle = batch && batch.productData &&
        batch.productData.gravimetricCalculationType === GravimetricCalculationType.Neat ?
        "Literature Density" : "Estimated Batch Density";

    //for total weight 
    let volumetricFlaskVisible = batch && batch.productData &&
        batch.productData.gravimetricCalculationType &&
        batch.productData.gravimetricCalculationType === GravimetricCalculationType.WV;

    let globalBalanceVisible = batch && batch.productData &&
        batch.productData.gravimetricCalculationType &&
        (batch.productData.gravimetricCalculationType === GravimetricCalculationType.WW ||
            batch.productData.gravimetricCalculationType === GravimetricCalculationType.WW_BD);

    let fillInstructionUomString = batch && batch.productData &&
        batch.productData.targetFillUomString && batch.productData.targetFillUomString.toUpperCase();

    let fillInstructions = `${batch && batch.productData &&
        batch.productData.targetFill ? batch.productData.targetFill : '0'} ± ` +
        `${batch && batch.productData &&
            batch.productData.targetFillUncertainty ? batch.productData.targetFillUncertainty : '0'} ` +
        `${fillInstructionUomString}`;

    /*
    let actualBatchSize = batch ? (batch.actualNumberUnits ? batch.actualNumberUnits : 0) +
        (batch.actualNumberRsUnits ? batch.actualNumberRsUnits : 0) : 0;
*/

    let actualBatchSize = () => {
        if (batch && batch.productData) {
            return (batch.actualNumberUnits ? batch.actualNumberUnits : 0) +
                (batch.productData.retentionSamplesNeeded ? batch.productData.retentionSamplesNeeded : 0);
        }

        return 0;
    }

    // yes this idiotic-ness is needed in TypeScript yay
    let elapsedMixTime = batch && batch.mixStartTime && batch.mixEndTime ?
        `${(new Date(batch.mixEndTime.toString()).getTime() - new Date(batch.mixStartTime.toString()).getTime()) / (60 * 1000)} minutes` : 'NA';

    /*        ORIG
        let elapsedMixTime = batch && batch.mixStartTime && batch.mixEndTime ?
            `${Math.abs(batch.mixEndTime.getTime() - batch.mixStartTime.getTime()) / (60 * 1000)} minutes` : 'NA';
            */

    let theoreticalYield = () => {
        if (batch && batch.actualWeight && batch.density && batch.productData && batch.productData.targetFill)
            return Math.round((batch.actualWeight / batch.density) / (batch.productData.targetFill * 1000));
        else
            return 'NA';
    };

    return (
        <div className="pw-content">
            <div className="lq-title-bar pw-form-header">
                <BenchSheetToolbar title="Production Bench Sheet"
                    entity={Entities.BatchProductions} leftInfo="Catalog #"
                    leftInfoSubtitle={batch && batch.productData ?
                        batch.productData.catalogNumber : ''}
                    rightInfo="Batch Name" benchSheetId=''
                    rightInfoSubtitle={batch && batch.batchName ?
                        batch.batchName : ''}
                    auditRowPointer={batchId}
                    documentationId={DocumentationConstants.ProductionBenchSheet}
                    showItemInformation={showProductionBenchSheetInformationDialog} />
            </div>

            {batch && batch.submissionDate &&
                <div className="lq-sub-title pw-margin-bottom-1">
                    <span className="pw-green">BATCH SUBMITTED: {PWDates.getDateTimeString(batch.submissionDate)}</span>
                </div>
            }

            <div id="pb-navigation-pane" >
                <div id="tab-buttons-panel" className="tab-buttons-container">
                    <div id="specPanelButton" onClick={onPanelButtonClick}>Specifications</div>
                    <div id="productionPanelButton" onClick={onPanelButtonClick}>Production</div>
                    <div id="appearancePanelButton" onClick={onPanelButtonClick}>Appearance</div>
                    <div id="labelingPanelButton" onClick={onPanelButtonClick}>Labeling</div>
                </div>
            </div>

            <div id="pb-content">
                <div id="specPanel">
                    <div className="pw-form-section">
                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Components Actual Layout</div>
                                <div className="pw-readonly-label">{batch && batch.productData ?
                                    batch.productData.gravimetricCalculationDescription : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Fill Instructions</div>
                                <div className="pw-readonly-label">{fillInstructions}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Preparation Instructions</div>
                                <div className="pw-readonly-label">{batch && batch.productData &&
                                    batch.productData.prepInstructions ?
                                    batch.productData.prepInstructions : 'NA'}</div>
                            </div>
                        </div>

                        {batch && batch.productData && batch.exhausted &&
                            <div className="pw-comp-row">
                                <div className="pw-form-unit">
                                    <div className="pw-normal pw-margin-left-05 pw-margin-bottom-1 pw-purple">Exhaust Batch</div>
                                </div>
                            </div>
                        }

                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Storage Requirements</div>
                                <div className="pw-readonly-label">{batch && batch.productData ?
                                    batch.productData.storage : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Maximum Percent Humidity</div>
                                <div className="pw-readonly-label">{batch && batch.productData ?
                                    batch.productData.humidity : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit">
                                <div className="pw-form-unit-label">Package Control</div>
                                <div className="pw-readonly-label">{batch && batch.productData ?
                                    batch.productData.packageControlName : 'Na'}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="productionPanel">

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-10">
                            <label className="pw-form-unit-label">Production Date</label>
                            <DateBox id="productionDate" type="date" displayFormat="MM/dd/yyyy" readOnly={!canEdit}
                                showClearButton={true}
                                defaultValue={null}
                                onValueChanged={onBatchValueChanged}
                                value={batch && batch.productionDate ? batch.productionDate : undefined}
                                placeholder="date" />
                        </div>

                    </div>

                    {volumetricFlaskVisible &&
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Closest Flask Size (mL)</div>
                                <div className="pw-plus-minus-uom">{targetBatchWeightCalculation &&
                                    targetBatchWeightCalculation.flaskVolume ?
                                    targetBatchWeightCalculation.flaskVolume.toLocaleString('en-US') : 'NA'}</div>

                                {/*
                                    <div className="pw-readonly-inline-label">{this.state.batch &&
                                        this.state.batch.closestFlaskSize ? this.state.batch.closestFlaskSize : 'NA'}</div>
                                        */}
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Volumetric Flask ID</div>
                                <div className="pw-readonly-label">{batch &&
                                    batch.volumetricFlask && batch.volumetricFlask.name ?
                                    batch.volumetricFlask.name : 'NA'}</div>
                            </div>

                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Volumetric Flask Size (mL)</div>
                                <div className="pw-readonly-inline-label">{batch &&
                                    batch.volumetricFlask && batch.volumetricFlask.volume ?
                                    batch.volumetricFlask.volume : 'NA'}</div>
                            </div>

                            <div className="pw-comp-row">
                                <div>
                                    <Button id="getVolumetricFlaskButton" disabled={!canEdit}
                                        text="Volumetric Flask" icon="find" type="success"
                                        hint="get volumetric flask"
                                        onClick={onVolumetricFlaskSearchClick} />
                                </div>
                            </div>
                        </div>
                    }

                    <div className="pw-comp-row">

                        {globalBalanceVisible &&
                            <div className="pw-form-unit pw-comp-20">
                                <label className="pw-form-unit-label">Total Weight Balance</label>
                                <SelectBox id="totalWeightBalanceId" placeholder='total weight balance'
                                    className="asset-selectbox" readOnly={!canEdit}
                                    dataSource={balances}
                                    valueExpr="assetId"
                                    displayExpr={(item) => {
                                        return item ?
                                            `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}`
                                            : "";
                                    }}
                                    value={batch && batch.totalWeightBalanceId ?
                                        batch.totalWeightBalanceId : undefined}
                                    itemRender={DataTemplates.InstrumentItemRender}
                                    onValueChanged={onBatchValueChanged}
                                />
                                <InstrumentSelectBoxTooltip target={"#totalWeightBalanceId"} instrument={balances?.find(a => a.assetId == batch?.totalWeightBalanceId)} />
                            </div>
                        }

                        <div className="pw-form-unit pw-comp-20">
                            <label className="pw-form-unit-label">Dispenser</label>
                            <SelectBox id="dispenserId" className="selectbox"
                                placeholder='dispenser' readOnly={!canEdit}
                                dataSource={dispensers}
                                itemRender={DataTemplates.InstrumentItemRender}
                                valueExpr="assetId"
                                displayExpr={(item) => {
                                    return item ?
                                        `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}`
                                        : "";
                                }}
                                value={batch && batch.dispenserId ? batch.dispenserId : null}
                                onValueChanged={onBatchValueChanged}
                                showClearButton={true}
                            />
                            <InstrumentSelectBoxTooltip target={"#dispenserId"} instrument={dispensers?.find(a => a.assetId == batch?.dispenserId)} />
                        </div>
                    </div>

                    <div id="components-actual-datagrid-container" className="pw-margin-bottom-1">
                        <ComponentsActualDataComponent batchId={batchId} balances={balances} isReadOnly={!canEdit}
                            onComponentsWeightChanged={onComponentsWeightChanged} />
                    </div>

                    <div>
                        <ProductionBenchSheetSolventsDataComponent batchId={batchId} isReadOnly={!canEdit}
                            matrixWeight={matrixWeight}
                            balances={balances} />
                    </div>

                    <div className="pw-comp-row pw-margin-top-1">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Target Batch Weight (mg)</div>

                            <div className="pw-plus-minus-uom">{targetBatchWeightCalculation &&
                                targetBatchWeightCalculation.targetBatchWeight ?
                                targetBatchWeightCalculation.targetBatchWeight.toLocaleString('en-US') : 'NA'}</div>
                        </div>

                        {batch && batch.productData &&
                            batch.productData.gravimetricCalculationType &&
                            batch.productData.gravimetricCalculationType !== GravimetricCalculationType.WV &&
                            <div id="totalBatchWeight" className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Total Batch Weight</div>
                                <NumberBox id="actualWeight" className="textbox"
                                    format="#0.0000" placeholder="NA" readOnly={!canEdit}
                                    onValueChanged={onBatchValueChanged}
                                    value={batch ? batch.actualWeight : 0} />
                            </div>
                        }

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Mix Start Time</div>
                            <DateBox id="mixStartTime" className="textbox" type="time"
                                defaultValue={null} displayFormat="HH:mm"
                                placeholder="NA" readOnly={!canEdit}
                                onValueChanged={onMixTimeChanged}
                                value={batch ? batch.mixStartTime : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Mix End Time</div>
                            <DateBox id="mixEndTime" className="textbox" type="time"
                                placeholder="NA" readOnly={!canEdit}
                                defaultValue={null} displayFormat="HH:mm"
                                onValueChanged={onMixTimeChanged}
                                value={batch ? batch.mixEndTime : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Elapsed Mix Time</div>
                            <div className="pw-readonly-label">{elapsedMixTime}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row pw-margin-left-05 pw-margin-right-05">
                        <div>
                            {/* PACKAGING MATERIAL DATAGRID */}
                            <DataGrid height="16rem"
                                wordWrapEnabled={true}
                                dataSource={batch && batch.packagingMaterials ?
                                    batch.packagingMaterials : []}
                                showBorders={true}
                                rowAlternationEnabled={true}
                                allowColumnResizing={true}
                                columnResizingMode='nextColumn'>

                                <LoadPanel enabled={true} />
                                <Selection mode={'single'} />
                                <Paging enabled={false} />

                                <Column type="buttons" width='5rem' >
                                    <ColumnButton name="delete" />
                                    <ColumnButton hint="audit information" icon="clock" />
                                    <ColumnButton hint="information" icon="info" />

                                    {/*
                                    onClick={this.onCompositionAuditButtonClicked}
                                    onClick={this.onCompositionInformationButtonClicked} 
                                    */}
                                </Column>

                                <Column dataField={'rawMaterialName'} width="15rem" />
                                <Column dataField={'rawMaterialDescription'} />

                                <Toolbar>
                                    <ToolBarItem location='before'>
                                        <div className="pw-datagrid-toolbar-template">
                                            <div className="pw-datagrid-toobar-title">Packaging Materials</div>
                                        </div>
                                    </ToolBarItem>
                                </Toolbar>

                                {/*
                                <Editing mode="popup" useIcons={true}
                                    allowUpdating={false} allowDeleting={canDelete} />
                                    */}

                            </DataGrid>
                        </div>
                    </div>

                    {/*
                        <div className="pw-comp-row">
                            {this.state.batch && this.state.batch.productData &&
                                this.state.batch.productData.gravimetricCalculationType &&
                                this.state.batch.productData.gravimetricCalculationType !== GravimetricCalculationType.WV &&
                                <div id="totalBatchWeight" className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Total Batch Weight</div>
                                    <NumberBox id="actualWeight" className="textbox"
                                        format="#0.0000" placeholder="NA" readOnly={!canEdit}
                                        onValueChanged={this.onBatchValueChanged}
                                        value={this.state.batch ? this.state.batch.actualWeight : 0} />
                                </div>
                            }
                        </div>
                        */}
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Planned Batch Size</div>
                            <div className="pw-readonly-label">{batch && batch.productData ?
                                batch.productData.plannedBatchSize : 'NA'}</div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">RS Units Needed</div>
                            <div className="pw-readonly-label">{batch && batch.productData ?
                                batch.productData.retentionSamplesNeeded : ''}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Actual Number of Units</div>
                            <NumberBox id="actualNumberUnits" className="textbox" readOnly={!canEdit}
                                format="#" placeholder="actual number of units"
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.actualNumberUnits : 0} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Actual Batch Size</div>
                            <div className="pw-readonly-label">{actualBatchSize()}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Theoretical Yield</div>
                            <div className="pw-readonly-inline-label">{theoreticalYield()}</div>
                        </div>
                    </div>

                    {/*
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Purge or Cleaning Estimate (mL)</div>
                            <div className="pw-readonly-inline-label">{batch
                                && batch.purgeOrCleaningEstimate ?
                                batch.purgeOrCleaningEstimate : '0'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Actual Cleaning Volume (mL)</div>
                            <NumberBox id="actualCleaningVolume" className="textbox"
                                format="#0.0000" placeholder="NA" readOnly={!canEdit}
                                onValueChanged={onBatchValueChanged}
                                value={batch ? batch.actualCleaningVolume : undefined} />
                        </div>
                    </div>
                    */}


                    <div className="pw-comp-row">
                        {(batch && batch.productData && batch.productData.gravimetricCalculationType !== GravimetricCalculationType.Neat) &&
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Measured Batch Density (DMA)</div>
                                <NumberBox id="density" className="textbox" readOnly={!canEdit}
                                    format="#0.00000" placeholder="density"
                                    onValueChanged={onBatchValueChanged}
                                    value={batch ? batch.density : 0} />
                            </div>
                        }

                        {(batch && batch.productData && batch.productData.gravimetricCalculationType === GravimetricCalculationType.Neat) &&
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-form-unit-label">Literature Density</div>
                                <div className="pw-plus-minus-uom">{batch && batch.productData &&
                                    batch.productData.estimatedBatchDensity} (g/mL)</div>
                            </div>
                        }
                    </div>

                    <div className="pw-margin-top-1">
                        <CheckWeightsComponent batchId={batchId} checkWeightData={checkWeightData}
                            isReadOnly={!canEdit} balances={balances}
                            onCheckWeightBalanceChanged={onCheckWeightBalanceChanged} />
                    </div>

                    <div className="pw-comp-row-scroll-fixed-grid">
                        <NoteViewer height='20rem' title="Production Bench Sheet Notes"
                            noteType={NoteType.ProductionBenchSheet} parentId={batchId} />
                    </div>

                    <div className="pw-comp-row-scroll-fixed-grid">
                        <ProductionBenchSheetPlanningNotesViewer headerColor='pw-bg-vc-600'
                            productId={batch && batch.productData && batch.productData.productId ?
                                batch.productData.productId : null} batchId={batchId} />
                    </div>

                    {/* CHANGE THIS TO NoteViewer */}
                    {/*
                    <div>
                        <div className="lq-header pw-margin-all-05 pw-width-auto">
                            <div className="lq-header-title">Preparation Notes</div>
                            <div className="title-bar-spacer" />
                        </div>

                        <div className="pw-margin-left-right-05">
                            <TextArea height='10rem' width='100%' readOnly={!canEdit}
                                value={batch && batch.preparationInstructions ?
                                    batch.preparationInstructions.text : ''}
                                onValueChanged={preparationNotesChanged} >
                            </TextArea>
                        </div>
                    </div>
                    */}

                    {batch && !batch.submissionDate &&
                        <div className="pw-margin-top-05">
                            <Button className="title-bar-button" disabled={!canEdit}
                                text="Submit Batch" icon="check" type="success"
                                hint="submit batch"
                                onClick={submitBatch} />
                        </div>
                    }


                    <div className="lq-sub-title pw-margin-top-1 pw-margin-bottom-05">Documents</div>
                    <DocumentLoader parentId={batchId}
                        documentType={DocumentType.ProductionBenchSheet}
                        readOnly={!canEdit} canDelete={canDelete} />

                    <DmaFooter />
                </div>

                <div id="appearancePanel" className="pw-form-section">
                    <AppearanceBenchSheetComponent batchId={batchId} canEdit={canEdit} />

                    <DmaFooter />
                </div>

                <div id="labelingPanel" className="pw-form-section">

                    <div>WIP</div>

                    <DmaFooter />
                </div>

            </div>

            {volumetricFlaskSearchDialogVisible &&
                <DialogPopup visible={volumetricFlaskSearchDialogVisible}
                    onHiding={hideVolumetricFlaskSearchDialog}
                    title="Volumetric Flask Search"
                    width={'55em'}
                    height={'40em'}>
                    <GlasswareSearch key={`vfs${key}`} glasswareType={GlasswareType.Volumetrics}
                        onCancel={hideVolumetricFlaskSearchDialog}
                        onApplySelection={onApplyVolumetricFlaskSelection} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit Information"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={auditEntity}
                        key={`AuditHistory${key}`}
                        rowPointer={auditRowPointer ? auditRowPointer : undefined} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="Product Planning" contextData={batch}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            <Toast visible={toastVisible}
                message={toastMessage}
                type={toastType}
                onHiding={onToastHiding}
                displayTime={3000} />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onComponentsWeightChanged(weight?: number) {

        console.log("Prod BS: onComponentsWeightChanged");
        console.log(componentsWeight);
        console.log(batch && batch.actualWeight ? batch.actualWeight : "No batch actual weight");

        componentsWeight = weight;

        calculateMatrixWeight();
    }

    function calculateMatrixWeight() {
        if (batch && batch.actualWeight) {
            const mWeight = batch.actualWeight -
                (componentsWeight ? componentsWeight : 0);

            setMatrixWeight(mWeight);
        }
    }

    function onCheckWeightBalanceChanged(fieldUpdate: FieldUpdate) {
        updateBatchField(fieldUpdate)
            .then(() => {
                getBatchCheckWeightData();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Check Weight Balance");
            })
    }

    async function submitBatch() {
        if (!dataLoaded)
            return;

        if (batchId) {
            let fieldUpdate: FieldUpdate = {
                field: "submissionDate",
                //value: new Date(),
                rowId: batchId
            };

            await updateBatchField(fieldUpdate)
                .then(() => {
                    if (batch && batch.productData && batch.productData.productId) {
                        setRedirectToProductFrom(true);
                    }
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Batch Submit Date");
                })
        }
    }

    /*
    // TODO: 
    async function getCALatestUpdates() {
        if (this.state.batch && this.state.batch.batchId) {
            await this.getComponentActualsAuditItems(this.state.batch.batchId)
                .then((data) => {
                    let auditItems = data as AuditItem[];

                    if (this.state.batch && this.state.batch.componentsActual &&
                        auditItems && auditItems.length > 0) {
                        const componentsActual = this.state.batch.componentsActual;

                        componentsActual.forEach((ca: ComponentActual) => {
                            let matchingWeight = auditItems.filter(a => a.keyId === ca.componentActualId
                                && a.fieldName.toLowerCase() === "weight");

                            if (matchingWeight && matchingWeight.length > 0) {
                                ca.latestWeightUpdate = matchingWeight[0].updateTimestamp;
                            }

                            //todo: also rawMaterialBatchId and balanceId
                        });

                        this.setState((prevState: any) => ({
                            batch: {
                                ...prevState.batch,
                                componentsActual: componentsActual
                            }
                        }));
                    }
                })
                .catch((error: ResponseError) => {
                    XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: error });
                })
        }
    }
    */

    function showBatchAuditDialog(e: any) {
        setAuditEntity(Entities.BatchProductions);
        setAuditRowPointer(batchId);
        setAuditInformationDialogVisible(true);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    //change events

    function onApplyVolumetricFlaskSelection(e: any) {
        hideVolumetricFlaskSearchDialog();

        if (e.glasswareItemId) {
            let fieldUpdate: FieldUpdate = {
                entity: "Batches",
                field: "volumetricFlaskId",
                value: e.glasswareItemId,
                rowId: batchId,
            };

            updateBatchField(fieldUpdate)
                .then(() => {
                    getVolumetricFlask(e.glasswareItemId);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Volumetric Flask");
                })
        }
    }

    function onBatchValueChanged(e: any) {
        if (!dataLoaded)
            return;

        const caller = e.element.id;

        setBatch({ ...batch, [caller]: e.value });

        let fieldUpdate: FieldUpdate = {
            field: caller,
            value: e.value,
            rowId: batchId
        };

        console.log("Requesting update for batch field: ", fieldUpdate);
        updateBatchField(fieldUpdate)
            .then(() => {
                if (caller === "density")
                    getBatchCheckWeightData();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Batch Field");
            })
    }

    function onMixTimeChanged(e: any) {
        if (!dataLoaded)
            return;

        const caller = e.element.id;
        setBatch({ ...batch, [caller]: e.value });

        let fieldUpdate: FieldUpdate = {
            field: caller,
            value: e.value,
            rowId: batchId
        };

        updateBatchField(fieldUpdate)
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Batch Field");
            })
    }

    //GET functions

    async function calculateTargetBatchWeightAndFlaskSize() {
        //todo: ADD
        //this.setState({ targetWeightsCalculationLoadIndicatorVisible: true });

        if (batchId) {
            await calculateTargetBatchWeight()
                .then((data) => {
                    //this.setState({ targetBatchWeightCalculation: data, targetWeightsCalculationLoadIndicatorVisible: false });


                    console.log("TBW");
                    console.log(data);

                    if(data)
                        setTargetBatchWeightCalculation(data.targetBatchWeightCalculation);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Calculating Target Batch Weight");
                })
        }

    }

    async function getBatch() {
        await CrudFunctions.getData(`products/batchproduction/${batchId}`)
            .then(data => {
                setBatch(data);
                calculateMatrixWeight();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Batch Data");
            });
    }
       
    async function calculateTargetBatchWeight() {
        return await CrudFunctions.getData(`calculations/batchtargetweight/${batchId}`);
    }
   
    async function getBatchCheckWeightData() {
        await CrudFunctions.getData(`calculations/checkweightdata/${batchId}`)
            .then(data => {
                if (data.MessageCode === ResultCodes.MissingData && data.Message) {
                    showToast(data.Message, "warning");
                }
                else {
                    setCheckWeightData(data);
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Check Weight Data");
            })
    }

    async function getBalances() {
        await CrudFunctions.getData("assets/allbalances")
            .then(data => {
                setBalances(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balances");
            })
    }

    async function getDispensers() {
        await CrudFunctions.getData("assets/alldispensers")
            .then(data => {
                setDispensers(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Dispensers");
            })
    }

    async function getVolumetricFlask(glasswareItemId: string) {
        await CrudFunctions.getData(`common/glassware/${glasswareItemId}`)
            .then(data => {
                setBatch({ ...batch, actualWeight: data.volume, volumetricFlask: data });
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Volumetric Flask");
            })
    }

    // PUT functions

    async function updateBatchField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("products/batchproductionfield", fieldUpdate);
    }

    async function updateCheckWeights(checkWeights: CheckWeight[]) {
        return await CrudFunctions.putData("products/checkweights", checkWeights);
    }

    //navigation

    function goBack() {
        history.back();
    }

    function onPanelButtonClick(e: any) {
        resetAllButtons();

        const button = e.target as HTMLDivElement;

        if (button) {
            button.className = "tab-button-active";
        }

        hideAllComponents();

        const id: string = e.target.id;
        const panel = document.getElementById(id.replace("Button", "")) as HTMLDivElement;
        panel.style.display = "";
    }

    function resetAllButtons() {
        const specPanelButton = document.getElementById("specPanelButton") as HTMLDivElement;
        specPanelButton.className = "tab-button-inactive";

        const productionPanelButton = document.getElementById("productionPanelButton") as HTMLDivElement;
        productionPanelButton.className = "tab-button-inactive";

        const appearancePanelButton = document.getElementById("appearancePanelButton") as HTMLDivElement;
        appearancePanelButton.className = "tab-button-inactive";

        const labelingPanelButton = document.getElementById("labelingPanelButton") as HTMLDivElement;
        labelingPanelButton.className = "tab-button-inactive";
    }

    function hideAllComponents(onload: boolean = false) {
        if (!onload) {
            const specPanel = document.getElementById("specPanel") as HTMLDivElement;
            specPanel.style.display = "none";
        }

        const productionPanel = document.getElementById("productionPanel") as HTMLDivElement;
        productionPanel.style.display = "none";

        const appearancePanel = document.getElementById("appearancePanel") as HTMLDivElement;
        appearancePanel.style.display = "none";

        const labelingPanel = document.getElementById("labelingPanel") as HTMLDivElement;
        labelingPanel.style.display = "none";
    }

    //show/hide

    function showVolumetricFlaskSearchDialog() {
        setVolumetricFlaskSearchDialogVisible(true);
    }

    function hideVolumetricFlaskSearchDialog() {
        setVolumetricFlaskSearchDialogVisible(false);
    }

    function showProductionBenchSheetInformationDialog() {
        if (batch) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: batchId,
                createTimestamp: batch.createTimestamp,
                lastModifiedTimestamp: batch.modifyTimestamp,
                createUser: batch.createUser,
                modifyUser: batch.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function onToastHiding() {
        setToastVisible(false);
        setToastMessage(undefined);
        setToastType(undefined);
    }

    function showToast(message: string, toastType: string) {
        setToastMessage(message);
        setToastType(toastType);
        setToastVisible(true);
    }

    //other

    function onVolumetricFlaskSearchClick(e: any) {
        showVolumetricFlaskSearchDialog();
    }

    function initSetTabs() {
        hideAllComponents(true);

        const mainPanelTab = document.getElementById("productionPanelButton") as HTMLDivElement;
        mainPanelTab.click();
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    async function getAllData() {
        await getBatch();
        await getBalances();
        await getDispensers();

        await Promise.all([
            getBatchCheckWeightData(),
            calculateTargetBatchWeightAndFlaskSize(),
            //getCALatestUpdates()
        ])
            .then(() => setDataLoaded(true));

    }
}

